import { useState } from "react";
import { Check, Copy, History, QrCode as QrIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useClipboard } from "@/lib/hooks/useClipboard";
import { useUtmLinks, type UtmLinkRow } from "@/lib/hooks/useUtmLinks";
import { QrCode } from "./QrCode";

/** Ultimas UTMs geradas pelo tenant, com copiar e QR sob demanda. */
export function UtmHistory(): JSX.Element {
  const { data: rows = [], isLoading, error } = useUtmLinks();
  const [openId, setOpenId] = useState<string | null>(null);
  const { copiedKey, copy } = useClipboard();

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-card" aria-labelledby="section-utm-history">
      <div className="flex items-center gap-2.5 border-b border-border bg-[hsl(var(--surface-raised)/0.4)] px-5 py-4">
        <History size={14} className="text-muted-foreground" />
        <h2 id="section-utm-history" className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Histórico de UTMs
        </h2>
      </div>
      <div className="p-5">
        {isLoading && <div className="h-12 animate-pulse rounded-lg bg-muted/40" aria-busy="true" />}
        {error && <p className="text-sm text-destructive">Não foi possível carregar o histórico: {error.message}</p>}
        {!isLoading && !error && rows.length === 0 && (
          <p className="text-sm text-muted-foreground">As UTMs que você gerar aparecem aqui.</p>
        )}
        <ul className="divide-y divide-border">
          {rows.map((row) => (
            <UtmHistoryItem
              key={row.id}
              row={row}
              open={openId === row.id}
              copied={copiedKey === row.id}
              onToggle={() => setOpenId(openId === row.id ? null : row.id)}
              onCopy={() => void copy(row.full_url, row.id)}
            />
          ))}
        </ul>
      </div>
    </section>
  );
}

interface UtmHistoryItemProps {
  readonly row: UtmLinkRow;
  readonly open: boolean;
  readonly copied: boolean;
  readonly onToggle: () => void;
  readonly onCopy: () => void;
}

function UtmHistoryItem({ row, open, copied, onToggle, onCopy }: UtmHistoryItemProps): JSX.Element {
  return (
    <li className="space-y-3 py-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-medium text-foreground">
            {row.utm_campaign} <span className="text-muted-foreground">· {row.utm_source} / {row.utm_medium}</span>
          </p>
          <p className="break-all font-mono text-xs text-muted-foreground">{row.full_url}</p>
        </div>
        <div className="flex gap-1">
          <Button type="button" size="sm" variant="ghost" onClick={onCopy} aria-label="Copiar URL">
            {copied ? <Check size={14} /> : <Copy size={14} />}
          </Button>
          <Button type="button" size="sm" variant="ghost" aria-expanded={open} aria-label="Mostrar QR Code" onClick={onToggle}>
            <QrIcon size={14} />
          </Button>
        </div>
      </div>
      {open && <QrCode value={row.full_url} label={`QR Code da campanha ${row.utm_campaign}`} />}
    </li>
  );
}
